/**
* CalendarHero - Floating header + gradient stat tiles
*/
import { ChevronLeft, ChevronRight, BarChart3, Edit3 } from "lucide-react";
import { formatCurrencyWhole } from "../../utils/formatters";
import { usePlan } from "../../utils/plan"; // plan_10

const CalendarHero = ({
  theme = 'pink',
  currentMonth,
  setCurrentMonth,
  noSpendCount,
  neutralCount,
  target,
  combinedNoSpendNeutral,
  totalSavedThisMonth,
  onShowYearlyStats,
  showYearlyStats = false,
  onInfoClick,
  onTargetClick,
  targetPopoverOpen = false
}) => {
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  const plan = usePlan(); // plan_10

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const shortMonthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const currentYear = currentMonth.getFullYear();
  const labelColor = isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
  const accentColor = isPink ? '#e879a0' : isWarm ? '#B07D50' : '#818cf8';
  const goodColor = '#2E9E7B';

  // Theme-aware tile palette — sourced from Budget's Income/Spent/Saved-Inv/Leftover tiles
  // (BudgetHero.jsx) so Calendar's tiles stay in the same family per theme.
  // Calendar uses Saved-Inv (green) + Income (pink) — no color shared with Wishlist's
  // Leftover (lavender) + Saved-Inv (green) pairing... except green, which is intentionally
  // shared between the two tabs (Layemi's call).
  const tileColors = {
    green: isPink
      ? { bg: 'linear-gradient(135deg, #E7F1DB 0%, #D3E5BE 100%)', text: '#5C7A4A' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #E9EFD9 0%, #DCE6C1 100%)', text: '#5C7A4A' }
      : { bg: 'linear-gradient(135deg, #DCEFF0 0%, #C9E4E6 100%)', text: '#2F7A80' },
    pink: isPink
      ? { bg: 'linear-gradient(135deg, #FAE3EC 0%, #F5D5E3 100%)', text: '#C23A72' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #F7E7D9 0%, #F0D8C2 100%)', text: '#B5651D' }
      : { bg: 'linear-gradient(135deg, #E1E8FA 0%, #D2DEF5 100%)', text: '#3A56C2' },
    lavender: isPink
      ? { bg: 'linear-gradient(135deg, #E9E1F5 0%, #D5C7EB 100%)', text: '#7A5FB0' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #F0E3D9 0%, #E5D0C2 100%)', text: '#9A5B3C' }
      : { bg: 'linear-gradient(135deg, #E4E1F5 0%, #D2CDEB 100%)', text: '#6B5FB0' },
  };

  // plan_10: Money plan - the mockup's top of Calendar.
  if (plan) {
    const card = { background: '#FFFFFF', borderRadius: 28, padding: 18, marginTop: 12 };
    const now = new Date();
    const pct = target > 0 ? Math.min(100, (combinedNoSpendNeutral / target) * 100) : 0;
    const hit = target > 0 && combinedNoSpendNeutral >= target;
    const ringC = 2 * Math.PI * 25;
    const navBtn = (dir) => (
      <button
        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + dir, 1))}
        className="flex items-center justify-center"
        style={{ width: 30, height: 36, border: 0, background: 'none' }}
        data-testid={dir < 0 ? 'prev-month-btn' : 'next-month-btn'}
        aria-label={dir < 0 ? 'Previous month' : 'Next month'}
      >
        {dir < 0 ? <ChevronLeft className="w-4 h-4" style={{ color: '#8B879A' }} /> : <ChevronRight className="w-4 h-4" style={{ color: '#8B879A' }} />}
      </button>
    );
    const pill = (onClick, label, on, testid) => (
      <button
        type="button"
        onClick={() => onClick && onClick()}
        data-testid={testid}
        style={{ flex: 1, background: on ? '#ECE6FA' : '#FFFFFF', color: on ? '#7A5DD3' : '#26252E', border: 0, borderRadius: 999, padding: '13px 10px', fontSize: 14, fontWeight: 600 }}
      >
        {label}
      </button>
    );
    return (
      <div className="pt-4 pb-2 wy-cal-hero" data-testid="calendar-hero">
        <div className="flex items-center justify-between" style={{ gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h1 className="flex items-center" style={{ margin: 0, gap: 8 }}>no-spend days<button type="button" aria-label="How it works" onClick={() => onInfoClick && onInfoClick()} style={{ background: '#FFFFFF', border: 0, cursor: 'pointer', borderRadius: '50%', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 600, color: '#6B6B73', padding: 0, flexShrink: 0 }}>i</button></h1>{/* plan_12: no subtitle; plan_41: the i */}
          </div>
          <div className="flex items-center flex-shrink-0 wy-month" style={{ height: 36, borderRadius: 999, background: '#FFFFFF', padding: '0 4px' }}>
            {navBtn(-1)}
            <span style={{ fontSize: 14, fontWeight: 500, color: '#8B879A', minWidth: 28, textAlign: 'center', whiteSpace: 'nowrap' }}>
              {currentYear === now.getFullYear() ? shortMonthNames[currentMonth.getMonth()] : `${shortMonthNames[currentMonth.getMonth()]} ${currentYear}`}
            </span>
            {navBtn(1)}
          </div>
        </div>

        <div style={card}>
          <div className="flex items-center justify-between" style={{ gap: 14 }}>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, letterSpacing: '-0.5px', color: '#26252E' }}>
                {noSpendCount} no-spend day{noSpendCount === 1 ? '' : 's'}
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#8B879A', lineHeight: 1.45 }}>
                {/* w2_15: say what counts toward the target */}
                {target > 0
                  ? `${noSpendCount} no-spend ${(combinedNoSpendNeutral - noSpendCount) > 0 ? `and ${combinedNoSpendNeutral - noSpendCount} neutral ` : ''}day${combinedNoSpendNeutral === 1 ? '' : 's'} count${combinedNoSpendNeutral === 1 ? 's' : ''} toward your target of ${target}.${hit ? ' You hit it!' : ''}`
                  : 'Set a monthly target for no-spend and neutral days.'}
              </p>
            </div>
            <svg viewBox="0 0 62 62" style={{ width: 56, height: 56, flex: 'none', transform: 'rotate(-90deg)' }} aria-hidden="true">
              <circle cx="31" cy="31" r="25" fill="none" stroke="#E6F4EC" strokeWidth="8" />
              {pct > 0 && <circle cx="31" cy="31" r="25" fill="none" stroke="#7FCBA0" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${(ringC * pct) / 100} ${ringC}`} />}
            </svg>
          </div>
          <div className="grid grid-cols-2" style={{ gap: 10, marginTop: 16 }}>
            <button
              type="button"
              onClick={() => onTargetClick && onTargetClick()}
              data-testid="target-stat"
              style={{ background: '#DFF2E7', borderRadius: 24, padding: 14, textAlign: 'left', border: 0 }}
            >
              <span className="flex items-center" style={{ gap: 5, fontSize: 12.5, color: '#55525F' }}>
                {'\u{1F3AF}'} No-spend + neutral <Edit3 style={{ width: 11, height: 11, color: '#2F8756', opacity: 0.7 }} />
              </span>
              <span style={{ display: 'block', fontSize: 19, fontWeight: 700, letterSpacing: '-0.5px', color: '#2F8756', marginTop: 3 }}>{combinedNoSpendNeutral} / {target}</span>
            </button>
            <div style={{ background: '#FAE4ED', borderRadius: 24, padding: 14 }}>
              <span style={{ display: 'block', fontSize: 12.5, color: '#55525F' }}>{'\u{1F3C6}'} Saved</span>
              <span style={{ display: 'block', fontSize: 19, fontWeight: 700, letterSpacing: '-0.5px', color: '#C04B7C', marginTop: 3 }}>{formatCurrencyWhole(totalSavedThisMonth)}</span>
            </div>
          </div>
        </div>

        <div className="flex" style={{ gap: 10, marginTop: 12 }}>
          {pill(onShowYearlyStats, `\u{1F4CA} ${currentYear} stats`, showYearlyStats, 'year-stats-btn')}
        </div>
      </div>
    );
  }

  return (
    <div className="pt-4 pb-2 wy-cal-hero wy-ph\" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Floating header — title left, month switcher right */}
      <div className="mb-3">
        <div className="flex items-center justify-between">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '28px', fontWeight: 400, color: '#1A1A1F', letterSpacing: '-0.6px' }}>Calendar</h1>
            <button onClick={() => onInfoClick && onInfoClick()} style={{ background: '#F4F1F8', border: '1px solid #EFEAF1', cursor: 'pointer', borderRadius: '50%', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#6B6B73', padding: 0, flexShrink: 0 }}>i</button>
          </div>
          <div className="flex items-center gap-0.5 wy-month">
            <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="w-4 h-4 flex items-center justify-center" data-testid="prev-month-btn">
              <ChevronLeft className="w-3.5 h-3.5" style={{ color: '#a39fa0' }} />
            </button>
            <span className="text-xs font-semibold px-1 text-center" style={{ color: '#a39fa0' }}>{currentYear === new Date().getFullYear() ? shortMonthNames[currentMonth.getMonth()] : `${shortMonthNames[currentMonth.getMonth()]} ${currentYear}`}</span>
            <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="w-4 h-4 flex items-center justify-center" data-testid="next-month-btn">
              <ChevronRight className="w-3.5 h-3.5" style={{ color: '#a39fa0' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Big focal number — floats directly on the page, no card, no competing button */}
      <div className="mb-1" data-testid="calendar-hero">
        <div className="flex items-baseline gap-2">
          <p className="hero-gradient-text" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '48px', fontWeight: 400, letterSpacing: '-2px', lineHeight: 1 }}>
            {noSpendCount}
          </p>
          <p style={{ fontSize: '18px', fontWeight: 400, color: '#6B6B73' }}>no-spend days</p>
        </div>
      </div>
      <div className="flex items-center justify-between" style={{ marginBottom: '14px' }}>
        <p style={{ fontSize: '13px' }}>
          <span style={{ color: tileColors.pink.text, fontWeight: 600 }}>{combinedNoSpendNeutral} of {target}</span>
          <span style={{ color: '#9A9AA8' }}> toward your monthly target</span>
        </p>
        <button
          onClick={onShowYearlyStats}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap border flex-shrink-0"
          style={showYearlyStats
            ? { background: tileColors.lavender.text, color: '#fff', borderColor: 'transparent' }
            : { background: tileColors.lavender.bg, color: tileColors.lavender.text, borderColor: 'transparent' }}
          data-testid="year-stats-btn"
        >
          <BarChart3 className="w-3.5 h-3.5" />
          <span>{currentYear} Stats</span>
        </button>
      </div>

      {/* Stat tiles — Target | Saved (sized to match Wealth Hero's tiles) */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button onClick={() => onTargetClick && onTargetClick()} data-testid="target-stat" className="rounded-[18px] text-left wy-tile wy-tile-ink" style={{ background: tileColors.green.bg, padding: '12px 11px 10px', border: 'none', cursor: 'pointer' }}>
          <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="1" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>🎯</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
            <span className="wy-tile-label" style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.green.text }}>Target</span>
            <Edit3 style={{ width: '9px', height: '9px', color: tileColors.green.text, opacity: 0.6 }} />
          </div>
          <p className="wy-tile-value" style={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.green.text }}>{combinedNoSpendNeutral} / {target}</p>
        </button>
        <div className="rounded-[18px] wy-tile wy-tile-ink" style={{ background: tileColors.pink.bg, padding: '12px 11px 10px' }}>
          <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="1" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>🏆</div>
          <span className="wy-tile-label" style={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.pink.text, marginBottom: '3px' }}>Saved</span>
          <p className="wy-tile-value" style={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.pink.text }}>{formatCurrencyWhole(totalSavedThisMonth)}</p>
        </div>
      </div>
    </div>
  );
};

export default CalendarHero;
